import {
  Children,
  FC,
  HTMLAttributes,
  PointerEvent,
  useRef,
  useState
} from 'react';
import { makeClass, setCSSVar } from '../../../utils';

import { CarouselPagination } from '../../jdscarouselpagination/generated';
import { Container } from '../Container';
import { Devices } from '../../../service/Devices';
import { TokensSpacingType } from '../models';

/**
 * pre-defined layouts
 */
export const LAYOUTS = [
  'full-width',
  '1-col-even',
  '2-col-even',
  '3-col-even',
  '4-col-even',
  '3-col-narrow-left-right',
  '2-col-narrow-left',
  '2-col-overflow',
  '3-col-overflow',
  '4-col-overflow'
];
export const FULL_LAYOUTS = ['full-width'];

const VerticalAlignmentCSSCustomMapping = {
  top: 'start',
  bottom: 'end'
};

export type ContentLayoutType = (typeof LAYOUTS)[number];

export interface ContentLayoutProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Determines the layout of `ContentLayout`.
   */
  layout?: ContentLayoutType;
  /**
   * Determines vertical alignment of `ContentLayout`.
   */
  verticalAlignment?: 'top' | 'center' | 'bottom' | 'stretch' | string;
  /**
   * Determines horizontal alignment of `ContentLayout`.
   */
  horizontalAlignment?: 'left' | 'center' | 'right' | 'stretch' | string;
  /**
   * Determines size of margin to add.
   */
  margin?: TokensSpacingType;
  /**
   * Determines the position of margin defined as part of `margin` property..
   */
  marginPosition?: 'vertical' | 'top' | 'bottom';
}

/**
 * The `<ContentLayout />` component extends `HTMLAttributes<HTMLDivElement>`.
 *
 * A content layout component with pre-defined layouts through react props.
 *
 * @param props
 * @constructor
 * @example
 *  <ContentLayout
    layout='2-col-even'
    horizontalAlignment='center'
    margin='m'
    marginPosition='top'
  >
    <Card
        size='s'
        title='Card'
        description='Lorem ipsum dolor sit amet,'
        background="secondary-30"
        primaryCTA={{ title: 'Primary Button' }}
      />
      <Card
        size='s'
        title='Card'
        description='Lorem ipsum dolor sit amet,'
        background="secondary-30"
        primaryCTA={{ title: 'Primary Button' }}
      />
  </ContentLayout>
  @version 1.0
 */
const ContentLayout: FC<ContentLayoutProps> = (props: ContentLayoutProps) => {
  const {
    className,
    children,
    layout = '1-col-even',
    verticalAlignment = 'stretch',
    horizontalAlignment = 'stretch',
    margin,
    marginPosition,
    ...rest
  } = props;
  const SLIDER_ENABLED_PANELS = [
    '2-col-overflow',
    '3-col-overflow',
    '4-col-overflow'
  ];

  const { tablet } = Devices.useMedia();

  const hasSlider = SLIDER_ENABLED_PANELS.includes(layout) && tablet;

  const classes = makeClass([
    className,
    FULL_LAYOUTS.includes(layout) ? 'l-layout--full' : 'l-layout--max-width',
    'j-content-layout',
    `j-content-layout__layout-${layout}`,
    margin && `sp--${margin}`,
    margin && marginPosition && `mg--${marginPosition}`,
    hasSlider && 'j-content-layout__slider'
  ]);

  const [activeIndex, setActiveIndex] = useState(0);

  const styleVars = setCSSVar({
    '--content-layout-horizontal-alignment': horizontalAlignment,
    '--content-layout-vertical-alignment': Object.keys(
      VerticalAlignmentCSSCustomMapping
    ).includes(verticalAlignment)
      ? VerticalAlignmentCSSCustomMapping[verticalAlignment]
      : verticalAlignment
  });
  const modifiedStyles = { ...(props.style || {}), ...styleVars };

  const contentPanelClasses = makeClass(['j-content-layout-panel']);

  const GAP_SIZE = 16;
  const TOUCH_THRESHOLD = 20;

  const touchRef = useRef<{
    start: number;
    end: number;
    id: number;
  }>({ start: 0, end: 0, id: 0 });

  const contentLayoutRef = useRef<HTMLDivElement>(null);

  const moveToPanel = (scrollValue: number) => {
    const currentActiveIndex =
      touchRef.current.id >= 0 ? touchRef.current.id : 0;
    Array.from(
      (contentLayoutRef.current?.children as HTMLCollectionOf<HTMLElement>) ||
        []
    ).forEach((item) => {
      item.style.transform = 'translateX(-' + scrollValue + 'px)';
    });
    setActiveIndex(currentActiveIndex);
  };

  const handlePointerStart = (e: PointerEvent<HTMLDivElement>): void => {
    if (hasSlider) {
      touchRef.current.start = e.clientX;
    }
  };

  const getScrolledValue = (panelItemIndex: number) => {
    let totalScrolled = 0;
    const items = Array.from(
      (contentLayoutRef.current?.children as HTMLCollectionOf<HTMLElement>) ||
        []
    );
    const panelIndexDifference = Math.abs(touchRef.current.id - panelItemIndex);
    items.length =
      panelItemIndex > 0
        ? panelIndexDifference === 0
          ? panelItemIndex
          : panelItemIndex - panelIndexDifference
        : 0;
    items.forEach((_, elIndex) => {
      totalScrolled = totalScrolled + items[elIndex].offsetWidth + GAP_SIZE;
    });
    return totalScrolled;
  };

  const handlePointerMove = (ev: PointerEvent<HTMLDivElement>): void => {
    if (hasSlider && touchRef.current.start) {
      touchRef.current.end = ev.clientX;
      if (ev.cancelable) ev.preventDefault();
      const touchDistance = touchRef.current.end - touchRef.current.start;
      const panelItemIndex = parseInt(
        ev.currentTarget.getAttribute('data-index') || '0'
      );

      const totalScrolled = getScrolledValue(panelItemIndex);
      const scrollValue = totalScrolled + -1 * touchDistance;

      moveToPanel(scrollValue);
    }
  };

  const handlePointerEnd = (ev: PointerEvent<HTMLDivElement>): void => {
    if (hasSlider && touchRef.current.end) {
      const touchDistance = touchRef.current.end - touchRef.current.start;
      const childItemsLength = contentLayoutRef.current?.children.length || 0;
      const touchDistancePercent =
        (100 / ev.currentTarget.offsetWidth) * Math.abs(touchDistance);
      let nextPanelID = touchRef.current.id < 0 ? 1 : touchRef.current.id;
      if (touchDistancePercent > TOUCH_THRESHOLD) {
        nextPanelID += touchDistance < 0 ? 1 : -1;
      }
      nextPanelID =
        nextPanelID >= childItemsLength ? nextPanelID - 1 : nextPanelID;
      touchRef.current.start = 0;
      slideToPanel(nextPanelID);
    }
  };

  const handlePointerOut = () => {
    touchRef.current.start = 0;
    slideToPanel(touchRef.current.id);
    touchRef.current.end = 0;
  };

  const slideToPanel = (index: number) => {
    touchRef.current.id = index;
    const scrollValue = getScrolledValue(index);
    moveToPanel(scrollValue);
  };

  return (
    <>
      <div
        {...rest}
        ref={contentLayoutRef}
        className={classes}
        style={modifiedStyles}
      >
        {Children.map(children, (child, index) => (
          <div
            onPointerDown={handlePointerStart}
            onPointerUp={handlePointerEnd}
            onPointerMove={handlePointerMove}
            onPointerOut={handlePointerOut}
            data-index={index}
            className={contentPanelClasses}
          >
            {child}
          </div>
        ))}
      </div>
      {hasSlider && (
        <Container
          className='pagination__wrapper'
          layout='centered'
          pad='xs'
          padPosition='vertical'
        >
          <CarouselPagination
            clickable
            onDotClick={slideToPanel}
            count={Children.count(children)}
            selectedIndex={activeIndex}
          />
        </Container>
      )}
    </>
  );
};

ContentLayout.defaultProps = {
  layout: '1-col-even',
  horizontalAlignment: 'stretch',
  verticalAlignment: 'stretch',
  margin: 'layout'
};

export default ContentLayout;
